import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Clock, CheckCircle, AlertCircle } from "lucide-react";

interface PhoneVerificationProps {
  phoneNumber: string;
  onVerified: () => void;
  onResend?: () => void;
  className?: string;
}

export default function PhoneVerification({
  phoneNumber,
  onVerified,
  onResend,
  className = ""
}: PhoneVerificationProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { toast } = useToast();

  // Countdown timer for code expiry
  useEffect(() => {
    if (!expiryTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((expiryTime.getTime() - now.getTime()) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0) {
        clearInterval(interval);
        toast({
          title: "Code Expired",
          description: "Please request a new verification code.",
          variant: "destructive",
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryTime, toast]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const interval = setInterval(() => {
      setResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleSendCode = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number first.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const response = await apiRequest("POST", "/api/auth/send-phone-verification", {
        phoneNumber: phoneNumber
      });

      if (response.ok) {
        const result = await response.json();

        // Set expiry time (10 minutes from now)
        const expiry = new Date(Date.now() + 10 * 60 * 1000);
        setExpiryTime(expiry);
        setTimeRemaining(600); // 10 minutes in seconds

        // Set resend cooldown (60 seconds)
        setResendCooldown(60);

        toast({
          title: "Verification Code Sent",
          description: `A 6-digit code has been sent to ${phoneNumber}`,
        });

        onResend?.();
      } else {
        const error = await response.json();
        throw new Error(error.message || "Failed to send verification code");
      }
    } catch (error: any) {
      toast({
        title: "Failed to Send Code",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter the 6-digit verification code.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      const response = await apiRequest("POST", "/api/auth/verify-phone", {
        phoneNumber: phoneNumber,
        code: verificationCode
      });

      if (response.ok) {
        const result = await response.json();
        setIsVerified(true);
        setExpiryTime(null);
        setTimeRemaining(0);

        toast({
          title: "Phone Verified",
          description: "Your phone number has been successfully verified!",
        });
        onVerified();
      } else {
        const error = await response.json();
        toast({
          title: "Invalid Code",
          description: error.message || "The verification code is incorrect. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-4xl mb-2">
              {isVerified ? "✅" : "📱"}
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isVerified ? "Phone Verified!" : "Verify Your Phone Number"}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {isVerified ? (
                "Your phone number has been successfully verified."
              ) : (
                <>
                  We'll send a verification code to <strong>{phoneNumber}</strong>
                </>
              )}
            </p>

            {timeRemaining > 0 && !isVerified && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <Clock className="w-4 h-4 text-orange-500" />
                <Badge variant="outline" className="text-orange-700 border-orange-300">
                  Expires in {formatTime(timeRemaining)}
                </Badge>
              </div>
            )}
          </div>

          {!isVerified && (
            <div className="space-y-3">
              <div className="flex space-x-2">
                <Button
                  onClick={handleSendCode}
                  disabled={isSending || resendCooldown > 0}
                  className="flex-1 bg-hydro-blue hover:bg-blue-600"
                  data-testid="button-send-code"
                >
                  {isSending ? (
                    "Sending..."
                  ) : resendCooldown > 0 ? (
                    `Resend in ${resendCooldown}s`
                  ) : expiryTime ? (
                    "Resend Code"
                  ) : (
                    "Send Verification Code"
                  )}
                </Button>
              </div>

              {expiryTime && (
                <div className="space-y-2">
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="text-center text-lg tracking-wider"
                    data-testid="input-verification-code"
                    disabled={timeRemaining === 0}
                  />

                  <Button
                    onClick={handleVerify}
                    disabled={isVerifying || verificationCode.length !== 6 || timeRemaining === 0}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="button-verify-code"
                  >
                    {isVerifying ? "Verifying..." : "Verify Code"}
                  </Button>
                </div>
              )}

              {expiryTime && timeRemaining === 0 && (
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                  <p className="text-sm text-red-700">
                    Verification code has expired. Please request a new one.
                  </p>
                </div>
              )}
            </div>
          )}

          {isVerified && (
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-green-700 font-medium">
                Phone number verified successfully!
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}